/**
 * Site inventory — what is on the ground, and the ability to correct it.
 *
 * Field report 2026-07-27: "I need a way to remove/edit the site inventory. I
 * accidentally added the trees and stuff twice so it's showing excess."
 *
 * The list was read-only. Rows arrived from a photo read or a survey import and
 * there was no way to fix a wrong count, change a species the model guessed, or
 * get rid of a row at all. A count that cannot be corrected is a count nobody
 * should trust, and this list feeds the takeoff.
 *
 * Removal is a tombstone, like every other record here. A row a client was
 * shown on a walk-through sheet is not erased because the list looked untidy.
 */
import { useEffect, useState } from 'preact/hooks';
import { listByJobIncludingRemoved, putRecord, softDelete } from '../../core/db';
import { nowIso } from '../../core/ids';
import type { Condition, InventoryCat, InventoryItem } from '../../core/types';
import { fmtQty } from '../../core/money';
import { toast } from '../state';
import { Empty, SourceChip, Tie } from '../bits';

const CATS: InventoryCat[] = ['tree', 'palm', 'shrub', 'cactus', 'hedge', 'bed', 'turf', 'valve', 'drain', 'other'];
const CONDS: Condition[] = ['good', 'fair', 'poor', 'dead', 'unknown'];

/** areas and lengths are measured; everything else is counted */
function unitFor(cat: InventoryCat): 'sqft' | 'linft' | 'each' {
  if (cat === 'turf' || cat === 'bed') return 'sqft';
  if (cat === 'hedge') return 'linft';
  return 'each';
}

const LIMIT = 120;

interface Props {
  jobId: string;
  /** live rows, already loaded with the rest of the job */
  items: InventoryItem[];
  user: string;
  /** kept on the parent so the tie still paints inside the §7 100ms budget */
  onToggleVerify: (it: InventoryItem) => void;
  onChanged: () => Promise<void>;
}

export function InventoryPanel({ jobId, items, user, onToggleVerify, onChanged }: Props) {
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [draft, setDraft] = useState<InventoryItem | null>(null);
  const [confirming, setConfirming] = useState<InventoryItem | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [busy, setBusy] = useState(false);

  const [removed, setRemoved] = useState<InventoryItem[]>([]);
  const live = items.filter((i) => !i.deletedAt);

  // tombstones are fetched only when asked for; they are not part of the job load
  useEffect(() => {
    if (!showRemoved) return;
    void (async () => {
      const all = await listByJobIncludingRemoved('inventoryItems', jobId);
      setRemoved(all.filter((i) => i.deletedAt).sort((a, b) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? '')));
    })();
  }, [showRemoved, jobId, items]);

  function startEdit(it: InventoryItem) {
    setEditing(it);
    setDraft({ ...it });
  }

  async function saveEdit() {
    if (!draft || busy) return;
    setBusy(true);
    try {
      // A row a person has corrected is that person's number now, whatever a
      // model said earlier. Recording that is what keeps a later re-read from
      // throwing the correction away (see readAerial's replace path).
      const touched = draft.cat !== editing?.cat || draft.species !== editing?.species
        || draft.qty !== editing?.qty || draft.cond !== editing?.cond || draft.notes !== editing?.notes;
      await putRecord('inventoryItems', {
        ...draft,
        qty: Number.isFinite(draft.qty) ? Math.max(0, draft.qty) : 0,
        species: draft.species?.trim() || undefined,
        notes: draft.notes?.trim() || undefined,
        provenance: touched
          ? { source: 'manual', setBy: user, setAt: nowIso(), detail: 'edited by hand' }
          : draft.provenance,
      });
      setEditing(null); setDraft(null);
      await onChanged();
      toast('Row updated.');
    } finally { setBusy(false); }
  }

  async function remove(it: InventoryItem) {
    setBusy(true);
    try {
      await softDelete('inventoryItems', it.id);
      setConfirming(null);
      await onChanged();
      toast(`${it.cat}${it.species ? ` — ${it.species}` : ''} removed. Nothing was destroyed — “show removed” puts it back.`);
    } finally { setBusy(false); }
  }

  async function restore(it: InventoryItem) {
    setBusy(true);
    try {
      await putRecord('inventoryItems', { ...it, deletedAt: null });
      await onChanged();
      toast('Row is back on the list.');
    } finally { setBusy(false); }
  }

  const scanned = live.filter((i) => i.provenance.source === 'scan');

  return (
    <section class="card" aria-label="Site inventory">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px;">
        <h2>Site inventory</h2>
        <label style="display:flex;align-items:center;gap:6px;font-weight:400;" class="small">
          <input type="checkbox" style="min-height:auto;" checked={showRemoved}
            onChange={() => setShowRemoved(!showRemoved)} /> show removed
        </label>
      </div>

      {live.length === 0 ? (
        <Empty>Nothing recorded on this property yet. Read the county photo on the map, or import a survey.</Empty>
      ) : (
        <div style="overflow-x:auto;">
          {live.length > LIMIT && (
            <p class="small muted" style="margin-bottom:6px;">
              Showing the first {LIMIT} of {live.length} rows. Everything is stored and appears in documents and exports.
            </p>
          )}
          {scanned.length > 0 && (
            <p class="small muted" style="margin-bottom:6px;">
              {scanned.length} row{scanned.length === 1 ? '' : 's'} came from a photo read and none of them are verified.
              Reading the photo again replaces them — anything you have verified or corrected by hand is kept.
            </p>
          )}
          <table class="data invtable">
            <thead><tr><th></th><th>Type</th><th>Species / detail</th><th class="money">Qty</th><th>Cond.</th><th>Notes</th><th>Src</th><th></th></tr></thead>
            <tbody>
              {live.slice(0, LIMIT).map((it) => (
                <tr key={it.id}>
                  <td><Tie verified={it.verification.verified} onToggle={() => onToggleVerify(it)} /></td>
                  <td style="font-weight:600;">{it.cat}</td>
                  <td>{it.species || <span class="muted">—</span>}</td>
                  <td class="money num">{fmtQty(it.qty, unitFor(it.cat) === 'sqft' ? 'sq ft' : unitFor(it.cat) === 'linft' ? 'lin ft' : 'each')}</td>
                  <td>{it.cond}</td>
                  <td class="muted small">{it.notes}</td>
                  <td><SourceChip source={it.provenance.source} /></td>
                  <td style="white-space:nowrap;">
                    <button class="btn btn-small btn-ghost" disabled={busy}
                      aria-label={`Edit ${it.cat} row`} onClick={() => startEdit(it)}>Edit</button>{' '}
                    <button class="btn btn-small btn-ghost" disabled={busy}
                      aria-label={`Remove ${it.cat} row`} onClick={() => setConfirming(it)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showRemoved && (
        <section style="margin-top:20px;" aria-label="Removed inventory">
          <h3 style="font-size:15px;margin-bottom:8px;">Removed</h3>
          {removed.length === 0 ? (
            <Empty>Nothing has been removed.</Empty>
          ) : (
            <table class="data">
              <thead><tr><th>Type</th><th>Species / detail</th><th class="money">Qty</th><th></th></tr></thead>
              <tbody>
                {removed.map((it) => (
                  <tr key={it.id} style="opacity:.7;">
                    <td>{it.cat}</td>
                    <td>{it.species || <span class="muted">—</span>}</td>
                    <td class="money num">{fmtQty(it.qty, 'each')}</td>
                    <td><button class="btn btn-small btn-ghost" disabled={busy}
                      aria-label={`Put ${it.cat} row back`} onClick={() => void restore(it)}>Put back</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {draft && (
        <div class="modal-backdrop" role="dialog" aria-modal="true" aria-label="Edit inventory row">
          <div class="modal">
            <h2 style="margin-bottom:10px;">Edit this row</h2>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
              <div class="field"><label>Type</label>
                <select aria-label="Row type" value={draft.cat}
                  onChange={(e) => setDraft({ ...draft, cat: (e.target as HTMLSelectElement).value as InventoryCat })}>
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div class="field"><label>Condition</label>
                <select aria-label="Row condition" value={draft.cond}
                  onChange={(e) => setDraft({ ...draft, cond: (e.target as HTMLSelectElement).value as Condition })}>
                  {CONDS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div class="field"><label>Species / detail</label>
                <input aria-label="Row species" value={draft.species ?? ''} style="width:100%;"
                  onInput={(e) => setDraft({ ...draft, species: (e.target as HTMLInputElement).value })} /></div>
              <div class="field">
                <label>How many ({unitFor(draft.cat) === 'sqft' ? 'sq ft' : unitFor(draft.cat) === 'linft' ? 'lin ft' : 'each'})</label>
                <input aria-label="Row quantity" type="number" inputMode="decimal" min="0" style="width:100%;"
                  value={String(draft.qty)}
                  onInput={(e) => {
                    const n = parseFloat((e.target as HTMLInputElement).value);
                    setDraft({ ...draft, qty: Number.isFinite(n) ? n : 0 });
                  }} /></div>
            </div>
            <div class="field" style="margin-top:10px;"><label>Notes</label>
              <textarea aria-label="Row notes" style="width:100%;min-height:70px;font-size:13.5px;" value={draft.notes ?? ''}
                onInput={(e) => setDraft({ ...draft, notes: (e.target as HTMLTextAreaElement).value })} /></div>
            <p class="small muted" style="margin:8px 0 12px;">
              A row you change becomes your number. A later photo read leaves it alone.
            </p>
            <div style="display:flex;gap:8px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={() => { setEditing(null); setDraft(null); }}>Cancel</button>
              <button class="btn btn-primary" disabled={busy} onClick={() => void saveEdit()}>Save row</button>
            </div>
          </div>
        </div>
      )}

      {confirming && (
        <div class="modal-backdrop" role="dialog" aria-modal="true" aria-label="Remove inventory row">
          <div class="modal">
            <h2 style="margin-bottom:10px;">Remove this {confirming.cat}?</h2>
            <p class="small" style="margin-bottom:12px;">
              It comes off the list and out of the takeoff drafts built from it. Nothing is destroyed — the row stays
              on this device and in your backups, and “show removed” puts it back. Documents already issued are untouched.
            </p>
            <div style="display:flex;gap:8px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={() => setConfirming(null)}>Cancel — keep it</button>
              <button class="btn btn-primary" disabled={busy} onClick={() => void remove(confirming)}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
